import React from 'react';

import { i18n } from 'config/locales';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { Summary, NameSummary, DateSummary } from 'components/Summary';
import { IDENTIFICATION, IDENTIFICATION_OTHER_NAMES } from 'config/formSections/identification';

import { EmptyListInstance } from 'forms/shared/models';
import Field from 'components/Form/Field';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';
import OtherNameItem from './OtherNameItem';

const sectionConfig = {
  key: IDENTIFICATION_OTHER_NAMES.key,
  section: IDENTIFICATION.name,
  store: IDENTIFICATION.store,
  subsection: IDENTIFICATION_OTHER_NAMES.name,
  storeKey: IDENTIFICATION_OTHER_NAMES.storeKey,
};

export class OtherNames extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.update = this.update.bind(this);
    this.updateBranch = this.updateBranch.bind(this);
    this.updateList = this.updateList.bind(this);
    this.updateComments = this.updateComments.bind(this);
    this.updateSummaryComments = this.updateSummaryComments.bind(this);
    this.summary = this.summary.bind(this);
  }

  update(queue) {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasOtherNames: this.props.HasOtherNames,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...queue,
    });
  }

  updateBranch(values) {
    this.update({
      HasOtherNames: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  }

  updateList(values) {
    this.update({
      List: values,
    });
  }

  updateComments(values) {
    this.update({
      Comments: values,
    });
  }

  updateSummaryComments(values) {
    this.update({
      SummaryComments: values,
    });
  }

  /**
   * Assists in rendering the summary section.
   */
  summary = (item, index) => {
    const itemObj = item.Item || {};
    const dates = DateSummary(itemObj.DatesUsed);
    const name = NameSummary(itemObj.Name);

    return Summary({
      type: i18n.t('identification.othernames.collection.summary.name'),
      index,
      left: name,
      right: dates,
      placeholder: i18n.t('identification.othernames.collection.summary.unknown'),
    });
  };

  render() {
    const { errors, formType, formVersion } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div
        className="section-content other-names"
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_OTHER_NAMES.key}
      >
        <h1 className="section-header">{i18n.t('identification.pvq.destination.othernames')}</h1>
        <Field
          titleSize="h3"
          optional
          className="no-margin-bottom"
        >
          {i18n.m('identification.pvq.othernames.info')}
        </Field>
        <Branch
          name="has_othernames"
          label={i18n.t('identification.pvq.othernames.branch.question')}
          labelSize="h4"
          {...this.props.HasOtherNames}
          warning
          onUpdate={this.updateBranch} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          attribute="HasOtherNames"
        />
        <Show when={this.props.HasOtherNames.value === 'Yes'}>
          <Accordion
            name="othernames"
            {...this.props.List}
            defaultState={this.props.defaultState}
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            errors={accordionErrors}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            description={i18n.t('identification.pvq.othernames.collection.summary.title')}
            appendTitle={i18n.t('identification.pvq.othernames.collection.appendTitle')}
            appendLabel={i18n.t('identification.pvq.othernames.collection.append')}
            titleSize="h4"
          >
            <OtherNameItem
              name="Item"
              requireIdentificationOtherNameExplanation={
                this.props.requireIdentificationOtherNameExplanation
              }
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={formType}
              formVersion={formVersion}
              bind
            />
          </Accordion>
        </Show>
      </div>
    ) : (
      <div
        className="section-content other-names"
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_OTHER_NAMES.key}
      >
        <h1 className="section-header">{i18n.t('identification.destination.othernames')}</h1>
        <Field
          title={i18n.t('identification.othernames.title')}
          titleSize="h3"
          optional
          help="identification.othernames.branch.help"
          className="no-margin-bottom"
        >
          {i18n.m('identification.othernames.info')}
        </Field>
        <Branch
          name="has_othernames"
          label={i18n.t('identification.othernames.branch.question')}
          labelSize="h4"
          {...this.props.HasOtherNames}
          warning
          onUpdate={this.updateBranch} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          attribute="HasOtherNames"
        />
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('identification.othernames.comment')}
            name="OptionalComment"
            attribute="OptionalComment"
            className="optional-comment"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}
        <Show when={this.props.HasOtherNames.value === 'Yes'}>
          <Accordion
            name="othernames"
            {...this.props.List}
            defaultState={this.props.defaultState}
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            errors={accordionErrors}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            description={i18n.t('identification.othernames.collection.summary.title')}
            appendTitle={i18n.t('identification.othernames.collection.appendTitle')}
            appendLabel={i18n.t('identification.othernames.collection.append')}
            titleSize="h4"
          >
            <OtherNameItem
              name="Item"
              requireIdentificationOtherNameExplanation={
                this.props.requireIdentificationOtherNameExplanation
              }
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={formType}
              formVersion={formVersion}
              bind
            />
          </Accordion>
          {!isFormSF852013(formType, formVersion) && (
            <Comments
              title={i18n.t('identification.othernames.summarycomment')}
              name="SummaryComment"
              attribute="SummaryComment"
              className="summary-comment"
              {...this.props.SummaryComments}
              onUpdate={this.updateSummaryComments}
              required={false}
            />
          )}
        </Show>
      </div>
    );
  }
}

OtherNames.defaultProps = {
  formType: '',
  formVersion: '',
  List: EmptyListInstance,
  Comments: {},
  SummaryComments: {},
  HasOtherNames: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  defaultState: true,
  required: false,
  errors: [],
};

export default connectSubsection(OtherNames, sectionConfig);
